import React, { useContext, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import GlobalState from "contexts/GlobalState"
import Breadcrumbs from "components/Common/Breadcrumb"
import QuickGuide from "components/QuickGuides/QuickGuide"
import TagsTree from "components/DropdownList/TagsTree"
import SelectGroupFilters from "components/DropdownList/SelectGroupFilters"
import VideoAppSettingsModal from "./VideoAppSettingsModal"
import { tableDefaultHeight } from "constants/layout"
import { getVideosWithSettings } from "helpers/backend_helper"
import {
  showToast,
  downloadExcel,
  stringSorter,
  numberSorter,
  defaultPageSizeOptions,
  searchToURL,
  booleanSorter,
  dateSorter,
  dateFormatter,
  getLottieSearch,
  exportPDF,
} from "utils/general"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
  Collapse,
  FormGroup,
  Alert,
} from "reactstrap"
import { Table as AntdTable, Switch, Input as AntdInput } from "antd"
import { useLocation, useNavigate } from "react-router-dom"
import Lottie from "react-lottie"
const { Search } = AntdInput

const VideoAppSettings = props => {
  const [state] = useContext(GlobalState)
  const [isSystemAdmin, setIsSystemAdmin] = useState(false)
  const [clients, setClients] = useState([])
  const [filteredClients, setFilteredClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [firstFetch, setFirstFetch] = useState(true)
  const [searchText, setSearchText] = useState("")
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [menu, setMenu] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showFullTable, setShowFullTable] = useState(false)
  const [group, setGroup] = useState("")
  const [selectedTags, setSelectedTags] = useState([])
  const [title] = useState(props.t("Video app settings"))
  const [quickGuide, setQuickGuide] = useState(false)
  const [sendFailed, setSendFailed] = useState([])

  const search = useLocation().search
  const navigate = useNavigate()
  const location = useLocation()

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"
    const searchParam = new URLSearchParams(search).get("search")
    if (searchParam && searchParam != "null") setSearchText(searchParam.trim())
    abortRef.current = new AbortController()

    if (state.authUser != null) {
      const obj = JSON.parse(state.authUser)
      let role = obj.activeRole ? obj.activeRole : obj.role
      if (role === "system_admin") {
        setIsSystemAdmin(true)
      }
    }

    getDevices()
    return () => abortRef.current.abort()
  }, [])

  // Group changed
  useEffect(() => {
    onSelectChange([], [])
    //getDevices()
    setClients([])
    setFirstFetch(true)
  }, [group])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, clients])

  // Handle search function
  const handleSearch = () => {
    let searchTextLowercase = searchText.toLowerCase()
    let result = clients.filter(
      data =>
        data.name?.toLowerCase().includes(searchTextLowercase) ||
        data.serial?.toLowerCase().includes(searchTextLowercase) ||
        data.imei?.toLowerCase().includes(searchTextLowercase)
    )
    setFilteredClients(result)
  }

  // Get clients
  const getDevices = () => {
    if (!loading && group?.length > 0) {
      setLoading(true)
      setFirstFetch(false)
      getVideosWithSettings(abortRef.current, group.join())
        .then(result => {
          //console.log("Video app settings: ", result.clients)
          if (result.statusCode === 200 && result.clients != null) {
            setClients(result.clients)
          } else {
            setClients([])
          }
          setLoading(false)
        })
        .catch(err => {
          console.log(err)
          setClients([])
          setLoading(false)
        })
    }
  }

  // Failed to sent settings; show in alert
  const setFailedSettings = arr => {
    let failed = []
    if (arr && arr.length > 0) {
      for (const element of clients) {
        if (arr.includes(element.key)) {
          failed.push(element.name)
        }
      }
    }
    setSendFailed(failed)
  }

  // Table structure
  const columns = [
    {
      dataIndex: "online",
      title: props.t("Connection"),
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "online", sortOrder)
      },
      render: cell => {
        if (cell) return <i className="mdi mdi-check-circle text-success" />
        return <i className="mdi mdi-close-thick text-danger" />
      },
      align: "center",
      width: "80px",
      ellipsis: true,
    },
    ...(group?.length > 1
      ? [
          {
            dataIndex: "group",
            title: props.t("Group"),
            sorter: (a, b, sortOrder) => {
              return stringSorter(a, b, "group", sortOrder)
            },
            width: "160px",
            ellipsis: true,
          },
        ]
      : []),
    {
      dataIndex: "name",
      title: props.t("Customer"),
      defaultSortOrder: "ascend",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "name", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    ...(showFullTable && isSystemAdmin
      ? [
          {
            dataIndex: "serial",
            title: props.t("Serial"),
            sorter: (a, b, sortOrder) => {
              return stringSorter(a, b, "serial", sortOrder)
            },
            width: "165px",
            ellipsis: true,
          },
          {
            dataIndex: "imei",
            title: props.t("IMEI"),
            sorter: (a, b, sortOrder) => {
              return stringSorter(a, b, "imei", sortOrder)
            },
            width: "165px",
            ellipsis: true,
          },
          {
            dataIndex: "room",
            title: props.t("Number"),
            sorter: (a, b, sortOrder) => {
              return stringSorter(a, b, "room", sortOrder)
            },
            width: "100px",
            ellipsis: true,
          },
          {
            dataIndex: "settings_user_name_production",
            title: props.t("Username"),
            sorter: (a, b, sortOrder) => {
              return stringSorter(
                a,
                b,
                "settings_user_name_production",
                sortOrder
              )
            },
            width: "165px",
            ellipsis: true,
          },
          {
            dataIndex: "settings_user_name_demo",
            title: props.t("Username") + " Demo",
            sorter: (a, b, sortOrder) => {
              return stringSorter(a, b, "settings_user_name_demo", sortOrder)
            },
            width: "165px",
            ellipsis: true,
          },
        ]
      : []),
    {
      dataIndex: "brightness",
      title: props.t("Brightness"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["brightness"], b["brightness"], sortOrder)
      },
      render: (cell, row) => {
        return cell ? <span>{Math.round(cell)}%</span> : <></>
      },
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "volume",
      title: props.t("Volume"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "volume", sortOrder)
      },
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "automatic_answering",
      title: props.t("Auto answer"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "automatic_answering", sortOrder)
      },
      render: (cell, row) => {
        return cell ? (
          <span>{row.automatic_answer_time}s</span>
        ) : (
          <i className="mdi mdi-close-thick text-danger" />
        )
      },
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "screensaver_clock",
      title: props.t("Screensaver"),
      align: "center",
      render: (cell, row) => {
        if (!cell) return <span>{props.t("Nothing")}</span>
        if (row.analog_clock) return <span>{props.t("Analog clock")}</span>
        else return <span>{props.t("Digital clock")}</span>
      },
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "screensaver_delay",
      title: props.t("Screensaver delay"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "screensaver_delay", sortOrder)
      },
      render: (cell, row) => {
        return cell
      },
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "screensaver_daytime",
      title: props.t("Activity time"),
      align: "center",
      width: "180px",
      ellipsis: true,
    },
    {
      dataIndex: "daily_restart_time",
      title: props.t("Restart time"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "daily_restart_time", sortOrder)
      },
      width: "170px",
      ellipsis: true,
    },
    {
      dataIndex: "language",
      title: props.t("Language"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "language", sortOrder)
      },
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "volume_buttons",
      title: props.t("Volume buttons"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "volume_buttons", sortOrder)
      },
      render: cell => {
        if (cell == 1) return <i className="mdi mdi-check-bold text-primary" />
        else if (cell == 0)
          return <i className="mdi mdi-close-thick text-danger" />
        else return <></>
      },
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "show_pip",
      title: props.t("PiP"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "show_pip", sortOrder)
      },
      render: cell => {
        if (cell == 1) return <i className="mdi mdi-check-bold text-primary" />
        else if (cell == 0)
          return <i className="mdi mdi-close-thick text-danger" />
        else return <></>
      },
      width: "120px",
      ellipsis: true,
    },
    ...(isSystemAdmin
      ? [
          {
            dataIndex: "health_button",
            title: "Health",
            align: "center",
            sorter: (a, b, sortOrder) => {
              return booleanSorter(a, b, "health_button", sortOrder)
            },
            render: cell => {
              if (cell == 1)
                return <i className="mdi mdi-check-bold text-primary" />
              else if (cell == 0)
                return <i className="mdi mdi-close-thick text-danger" />
              else return <></>
            },
            width: "120px",
            ellipsis: true,
          },
          {
            dataIndex: "show_debug",
            title: props.t("Debug"),
            align: "center",
            sorter: (a, b, sortOrder) => {
              return booleanSorter(a, b, "show_debug", sortOrder)
            },
            render: cell => {
              if (cell == 1)
                return <i className="mdi mdi-check-bold text-primary" />
              else if (cell == 0)
                return <i className="mdi mdi-close-thick text-danger" />
              else return <></>
            },
            width: "120px",
            ellipsis: true,
          },
          {
            dataIndex: "assisted_restart",
            title: props.t("Assisted restart"),
            align: "center",
            sorter: (a, b, sortOrder) => {
              return booleanSorter(a, b, "assisted_restart", sortOrder)
            },
            render: cell => {
              if (cell == 1)
                return <i className="mdi mdi-check-bold text-primary" />
              else if (cell == 0)
                return <i className="mdi mdi-close-thick text-danger" />
              else return <></>
            },
            width: "120px",
            ellipsis: true,
          },
          {
            dataIndex: "uses_production",
            title: props.t("Production"),
            align: "center",
            sorter: (a, b, sortOrder) => {
              return booleanSorter(a, b, "uses_production", sortOrder)
            },
            render: cell => {
              if (cell == 1)
                return <i className="mdi mdi-check-bold text-primary" />
              else if (cell == 0)
                return <i className="mdi mdi-close-thick text-danger" />
              else return <></>
            },
            width: "120px",
            ellipsis: true,
          },
        ]
      : []),
    {
      dataIndex: "sync_time",
      title: props.t("Synced"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["sync_time"], b["sync_time"], sortOrder)
      },
      render: dateFormatter,
      width: "160px",
      ellipsis: true,
    },
  ]

  // Export data
  const exportData = (type = "excel") => {
    const heading = [
      props.t("Customer"),
      props.t("Brightness"),
      props.t("Volume"),
      props.t("Volume buttons"),
      props.t("Auto answer"),
      props.t("Answer time"),
      props.t("Screensaver"),
      props.t("Screensaver delay"),
      props.t("Screensaver active"),
      props.t("Restart time"),
      props.t("Language"),
      props.t("PiP"),
      props.t("Synced"),
    ]
    const data = filteredClients.map(elt => [
      elt.name,
      elt.brightness,
      elt.volume,
      elt.volume_buttons,
      elt.automatic_answering,
      elt.automatic_answer_time,
      props.t(elt.screensaver_content),
      elt.screensaver_delay,
      elt.screensaver_daytime,
      elt.daily_restart_time,
      elt.language,
      elt.show_pip,
      dateFormatter(elt.sync_time),
    ])

    if (type == "excel") {
      downloadExcel(title, [heading], data)
      showToast(props.t("Excel file exported succesfully!"), "success")
    } else if (type == "pdf") {
      exportPDF(title, heading, data)
      showToast(props.t("PDF file exported succesfully!"), "success")
    }
  }

  // Table selection changed
  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys)
    setSelectedRows(newSelectedRows)
  }

  return (
    <>
      <div className="page-content">
        <Container fluid className={quickGuide ? "quick-guide-open" : ""}>
          <Breadcrumbs
            title={props.t("Video app settings")}
            breadcrumbItem={
              <>
                <div className="d-inline-block">
                  <QuickGuide
                    source={"videoAppSettings"}
                    title="Video app settings"
                    isOpen={quickGuide}
                    setIsOpen={setQuickGuide}
                  />
                </div>
              </>
            }
          />

          <Row className="mb-4">
            <Col xs={12} md={3} className="mb-2 mb-md-0">
              <div className="d-inline-block" style={{ width: "100%" }}>
                <TagsTree
                  loadData={true}
                  expanded={true}
                  selectionChanged={setSelectedTags}
                />
              </div>
            </Col>
            <Col xs={12} md={6} className="mb-2 mb-md-0">
              <SelectGroupFilters
                onChange={setGroup}
                multiple={true}
                disabled={loading}
                selectedTags={selectedTags}
              />
            </Col>
            <Col xs={12} md={3}>
              <span className="d-flex">
                <Button
                  disabled={loading}
                  color="primary"
                  style={{
                    minWidth: "80px",
                    height: "32px",
                    padding: "inherit",
                  }}
                  onClick={() => {
                    getDevices()
                  }}
                >
                  {props.t("Search")}
                </Button>
              </span>
            </Col>
          </Row>

          {(!group || group?.length == 0 || firstFetch) && !loading && (
            <Card>
              <CardBody>
                <div className="text-center my-5">
                  <Lottie
                    options={getLottieSearch()}
                    height={150}
                    width={180}
                  ></Lottie>
                </div>
              </CardBody>
            </Card>
          )}

          {!!group && group.length > 0 && !firstFetch && (
            <Card>
              <CardBody>
                <Row>
                  <Col>
                    {clients != null && (
                      <React.Fragment>
                        <Row className="mb-1">
                          <Col className="mb-3" style={{ maxWidth: "320px" }}>
                            <Search
                              placeholder={props.t("Search")}
                              allowClear
                              value={searchText}
                              onChange={event => {
                                setSearchText(event.target.value)
                              }}
                              onBlur={() =>
                                // Apply search word to URL
                                searchToURL(searchText, location, navigate)
                              }
                              style={{ minWidth: "240px" }}
                            />
                          </Col>

                          {isSystemAdmin && (
                            <Col className="mt-1 col-auto">
                              <FormGroup>
                                <Switch
                                  id="showFullSwitch"
                                  name="showFullTable"
                                  checked={showFullTable}
                                  disabled={loading}
                                  onChange={v => {
                                    setShowFullTable(v)
                                  }}
                                  checkedChildren={
                                    <div
                                      className="d-flex"
                                      style={{ alignItems: "center" }}
                                    >
                                      <i
                                        className="bx bx-table font-size-16"
                                        style={{ marginTop: "3px" }}
                                      />
                                    </div>
                                  }
                                  unCheckedChildren={
                                    <div
                                      className="d-flex"
                                      style={{ alignItems: "center" }}
                                    >
                                      <i
                                        className="bx bx-table font-size-16"
                                        style={{ marginTop: "3px" }}
                                      />
                                    </div>
                                  }
                                ></Switch>
                                <UncontrolledTooltip
                                  placement="right"
                                  target="showFullSwitch"
                                >
                                  {props.t("Show all columns")}
                                </UncontrolledTooltip>
                              </FormGroup>
                            </Col>
                          )}
                          <Col>
                            <span className="float-end ms-2">
                              <UncontrolledTooltip
                                placement="top"
                                target="downloadstatstable"
                              >
                                {props.t("Download statistics")}
                              </UncontrolledTooltip>
                              <Dropdown
                                id="printButtonServicesStats"
                                isOpen={menu}
                                toggle={() => setMenu(!menu)}
                                className="download-btn text-center"
                                type="button"
                              >
                                <DropdownToggle
                                  id="downloadstatstable"
                                  className="text-muted"
                                  tag="i"
                                >
                                  <i className="mdi mdi-download" />
                                </DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem
                                    onClick={() => exportData("excel")}
                                  >
                                    {props.t("Download .xlsx")}
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => exportData("pdf")}
                                  >
                                    {props.t("Download .pdf")}
                                  </DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </span>
                            <div className="mt-2 float-end">
                              <p>
                                {filteredClients.length}{" "}
                                {props.t("found results")}
                              </p>
                            </div>
                          </Col>
                        </Row>

                        <Collapse isOpen={sendFailed.length > 0}>
                          <Alert color="danger">
                            <div>
                              <h5 className="alert-heading">
                                {props.t("Sending failed") + ":"}
                              </h5>
                              <span>{sendFailed.join(", ")}</span>
                            </div>
                          </Alert>
                        </Collapse>

                        <Collapse isOpen={selectedRowKeys.length > 0}>
                          <Row className="py-2">
                            <Col className="col-auto">
                              <Button
                                color="primary"
                                //disabled={selectedRowKeys.length == 0 || loading}
                                onClick={() => {
                                  setShowModal(!showModal)
                                }}
                              >
                                <i className="mdi mdi-cog me-2" />
                                {props.t("Change settings")}
                              </Button>
                            </Col>
                            <Col style={{ alignContent: "center" }}>
                              <span>
                                {selectedRowKeys.length +
                                  " " +
                                  props.t("selected clients")}
                              </span>
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                className="ms-2"
                                onClick={() => {
                                  onSelectChange([], [])
                                }}
                              >
                                {props.t("Clear selections")}
                              </Button>
                            </Col>
                          </Row>
                        </Collapse>

                        <Row className="mt-2">
                          <Col xl="12">
                            <AntdTable
                              className="colored-header narrow-header"
                              bordered
                              size="small"
                              rowKey={"key"}
                              columns={columns}
                              dataSource={filteredClients}
                              rowSelection={{
                                selectedRowKeys,
                                onChange: onSelectChange,
                                getCheckboxProps: record => ({
                                  disabled: !record.online,
                                }),
                              }}
                              pagination={{
                                showSizeChanger: true,
                                defaultPageSize: 50,
                                pageSizeOptions: defaultPageSizeOptions,
                              }}
                              loading={loading}
                              tableLayout="auto"
                              scroll={{
                                x: "max-content",
                                y: tableDefaultHeight,
                              }}
                            />
                          </Col>
                        </Row>
                      </React.Fragment>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}
        </Container>
      </div>

      <VideoAppSettingsModal
        keys={selectedRowKeys}
        clients={selectedRows}
        showModal={showModal}
        setShowModal={setShowModal}
        setFailed={setFailedSettings}
        isSystemAdmin={isSystemAdmin}
      ></VideoAppSettingsModal>
    </>
  )
}

VideoAppSettings.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(VideoAppSettings)
